import { isSalesViewEnabled } from '@ab-core-functions/sales-view';
import { userHasOnlySalesView as userHasOnlySalesViewFunction } from '@ab-core-functions/user-permission';
import { formatNumber, formatPrice } from '@ab-core/functions';
import type { MaterialPriceSurcharge, PositionPriceObject } from '@ab-core/graphql/dist/index';
import { useAuth } from '@ab-core/hooks';
import useText from '@ab-core/localization/src/useText';
import Spacer from '@ab-core/spacer';
import Text, { FontTypes } from '@ab-core/text';
import type { FC } from 'react';
import React from 'react';
import { Colum, Divider } from '.././styled';
import { ScalePrices } from '../scalePrices';

const ONE_HUNDRED = 100;
const NUMBER_OF_DIGITS_AFTER_DECIMAL_POINT = 3;

type SurchargesProps = Pick<
    PositionPriceObject,
    'materialPriceSurcharges' | 'priceObject' | 'value' | 'productPriceSum' | 'quantity'
> & {
    quantityUnit: string;
};

export const SurchargesAndScalePrices: FC<SurchargesProps> = (props) => {
    const { productPriceSum, quantity, materialPriceSurcharges, quantityUnit, value, priceObject } = props;
    const { user } = useAuth();
    const isSalesView = isSalesViewEnabled(user?.activeAccount?.accountNumber);
    const userHasOnlySalesView = userHasOnlySalesViewFunction();
    const getText = (surcharge: MaterialPriceSurcharge): string => {
        let text = `Basis: ${surcharge?.basePrice?.centAmount && surcharge?.basePrice?.centAmount / ONE_HUNDRED}`;

        if (surcharge?.weight) {
            text += `, Gewicht: ${
                surcharge?.weight?.toFixed(NUMBER_OF_DIGITS_AFTER_DECIMAL_POINT).replace('.', ',') || '-'
            } kg`;
        }

        return text;
    };
    const priceQuantityLabel = useText('priceCalculation.priceQuantityLabel');

    return (
        <>
            <Text type={FontTypes.Title} content={useText('priceCalculation.surcharges')} />

            {productPriceSum && (
                <Colum>
                    <Text content={`${priceQuantityLabel} ${formatNumber(quantity)} ${quantityUnit}`} />
                    <Text content={formatPrice(productPriceSum)} />
                </Colum>
            )}

            {priceObject?.scalePrices && !isSalesView && !userHasOnlySalesView && (
                <>
                    <Spacer size="xs" />
                    <Divider />
                    <ScalePrices priceObject={priceObject} quantity={quantity || 1} quantityUnit={quantityUnit} />
                </>
            )}

            {materialPriceSurcharges && (
                <>
                    <Spacer size="xs" />
                    <Divider />
                    <Colum>
                        <Text type={FontTypes.Subtitle} content="Zuschläge" />
                    </Colum>

                    {materialPriceSurcharges?.map((surcharge: MaterialPriceSurcharge, i: number) => (
                        <React.Fragment key={i}>
                            <Colum>
                                {surcharge.value && (
                                    <>
                                        <Text content={surcharge.type} />
                                        <Text content={formatPrice(surcharge.value)} />
                                    </>
                                )}
                            </Colum>
                            <Colum>
                                <Text type={FontTypes.Subtitle} color="gray70" content={getText(surcharge)} />
                            </Colum>
                            <Spacer size="xs" />
                        </React.Fragment>
                    ))}
                </>
            )}

            {value && (
                <>
                    <Spacer size="xs" />
                    <Divider />
                    <Spacer size="xs" />
                    <Colum>
                        <Text type={FontTypes.Title} content={useText('priceCalculation.cartPrice')} />
                        <Text type={FontTypes.Price} content={formatPrice(value)} />
                    </Colum>
                </>
            )}
            {priceObject?.surchargeCut?.value && (
                <Colum>
                    <Text type={FontTypes.Caption} color="gray70" content="zzgl. Schnittkosten" />
                    <Text color="gray70" content={`+ ${formatPrice(priceObject?.surchargeCut.value)}`} />
                </Colum>
            )}
        </>
    );
};
